import type { Frags_Form_Email_Settings_Fragment$data } from '@/graphql/queries/Frags.query';
import { ExpandMore } from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  Box,
  Checkbox,
  FormControlLabel,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { toast } from 'react-toastify';
import type { Writable } from 'ts-essentials';
import { EmailComposer } from './EmailComposer';
import type { FormBodyProps } from './FormBody';

const defaults = Object.freeze({
  visitorSubject: 'Thank you for submitting the form',
  visitorEmailBody: `Hi {{first_name}} {{last_name}},

Thank you for submitting the form. We will get back to you shortly.

Regards,
Zngly Team
`,
  adminSubject: 'New form submission',
  adminEmailBody: `Hi,

A new form has been submitted. Please check the form submission details below.

Form Title: {{form_title}}
Form Submitted Url: {{form_url}}
Form Settings Url: {{form_settings_url}}
Form Data: 

{{form_data}}

Regards`,
});

export const initFormEmailState: Omit<Frags_Form_Email_Settings_Fragment$data, ' $fragmentType'> = Object.freeze({
  visitor: {
    enabled: false,
    subject: defaults.visitorSubject,
    body: defaults.visitorEmailBody,
  },
  admins: {
    enabled: false,
    emails: [],
    subject: defaults.adminSubject,
    body: defaults.adminEmailBody,
  },
});

export type FormEmailState = Writable<typeof initFormEmailState>;

type Props = {
  disabled?: boolean;
  state?: FormEmailState | null;
  setState: FormBodyProps['setState'];
};

const EmailOptions = ({ state, setState, disabled }: Props) => {
  return (
    <Box
      sx={{
        border: '1px solid rgb(185 185 185 / 87%)',
        borderRadius: '4px',
        boxShadow: 'none',
      }}
    >
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMore />} disabled={disabled}>
          <Typography>Notify Admins</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Stack direction="column" spacing={2} sx={{ m: '1em 0' }}>
            <Autocomplete
              multiple
              options={[]}
              freeSolo
              fullWidth
              value={(state?.admins?.emails as string[]) || []}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  const value = (event.target as HTMLInputElement).value;

                  if (value.length === 0 || !value) return;
                  // check if the email is valid
                  const isValidEmail = value.match(/^([\w.%+-]+)@([\w-]+\.)+([\w]{2,})$/i);
                  if (!isValidEmail) {
                    // Prevent's default 'Enter' behavior.
                    event.defaultMuiPrevented = true;
                    toast.error('Please enter a valid email address');
                    return;
                  }
                }
              }}
              onChange={(_, value) => {
                const enabled = value.length > 0;
                setState((prev) => ({
                  ...prev,
                  email: {
                    ...(prev?.email as NonNullable<typeof prev.email>),
                    admins: {
                      ...(prev?.email?.admins as NonNullable<NonNullable<typeof prev.email>['admins']>),
                      emails: value as string[],
                      enabled,
                    },
                  },
                }));
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  name="email"
                  type="email"
                  label="People to notify when form is submitted"
                  helperText="Enter Valid Email Addresses. Hit Enter to add more."
                  fullWidth
                  variant="outlined"
                />
              )}
            />

            {!!state?.admins?.emails?.length && (
              <EmailComposer
                subject={state?.admins?.subject ?? defaults.adminSubject}
                body={state?.admins?.body ?? defaults.adminEmailBody}
                onChange={(adminState) => {
                  setState((prev) => ({
                    ...prev,
                    email: {
                      ...(prev.email as NonNullable<typeof prev.email>),
                      admins: {
                        ...(prev.email?.admins as NonNullable<NonNullable<typeof prev.email>['admins']>),
                        ...adminState,
                      },
                    },
                  }));
                }}
              />
            )}
          </Stack>
        </AccordionDetails>
      </Accordion>
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMore />} disabled={disabled}>
          <Typography>Notify Recipient</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Stack direction="column" spacing={2} sx={{ m: '1em 0' }}>
            <FormControlLabel
              control={
                <Checkbox
                  className="zn-chbx-1"
                  checked={state?.visitor?.enabled ?? false}
                  onChange={({ target: { checked } }) => {
                    setState((prev) => ({
                      ...prev,
                      email: {
                        ...(prev.email as NonNullable<typeof prev.email>),
                        visitor: {
                          ...(prev.email?.visitor as NonNullable<NonNullable<typeof prev.email>['visitor']>),
                          enabled: checked,
                        },
                      },
                    }));
                  }}
                />
              }
              label="Send email to the recipient when they submit the form"
            />

            {state?.visitor?.enabled && (
              <EmailComposer
                subject={state?.visitor?.subject ?? defaults.visitorSubject}
                body={state?.visitor?.body ?? defaults.visitorEmailBody}
                onChange={(visitorState) => {
                  setState((prev) => ({
                    ...prev,
                    email: {
                      ...(prev.email as NonNullable<typeof prev.email>),
                      visitor: {
                        ...(prev.email?.visitor as NonNullable<NonNullable<typeof prev.email>['visitor']>),
                        ...visitorState,
                      },
                    },
                  }));
                }}
              />
            )}
          </Stack>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

export default EmailOptions;
